import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ChevronDown, User, Users } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'

export interface SubjectListItemData {
  readonly id: string
  readonly primaryIdentifier: string
  readonly displayName?: string
  readonly email?: string
  readonly status: string
  readonly identifierType?: string
  readonly matchedAttribute?: {
    readonly key: string
    readonly value: unknown
    readonly dataType: string
  }
}

export interface SubjectListCardData {
  readonly message: string
  readonly subjects: readonly SubjectListItemData[]
  readonly pagination?: {
    readonly page: number
    readonly totalItems: number
    readonly pageSize: number
  }
}

const STATUS_STYLES: Record<string, string> = {
  ACTIVE: 'border-emerald-200 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50',
  INACTIVE: 'border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-950/50',
  SUSPENDED: 'border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-950/50',
}

const MAX_VISIBLE = 5

export function SubjectListCard({ data }: { data: SubjectListCardData }) {
  const [expanded, setExpanded] = useState(false)

  if (data.subjects.length === 0) {
    return (
      <div className="my-2 rounded-lg border border-border dark:border-border/40 bg-card shadow-sm px-3.5 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Users className="size-4" />
          <span>{data.message}</span>
        </div>
      </div>
    )
  }

  const needsCollapse = data.subjects.length > MAX_VISIBLE
  const visible = needsCollapse && !expanded ? data.subjects.slice(0, MAX_VISIBLE) : data.subjects
  const hiddenCount = data.subjects.length - MAX_VISIBLE

  return (
    <div className="my-2 space-y-1">
      <div className="flex items-center gap-2 px-1 py-1 text-xs text-muted-foreground">
        <Users className="size-3.5" />
        <span>{data.message}</span>
      </div>
      {visible.map((subject) => (
        <SubjectRow key={subject.id} subject={subject} />
      ))}
      {needsCollapse && !expanded && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full gap-1.5 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setExpanded(true)}
        >
          <ChevronDown className="size-3.5" />
          Show {hiddenCount} more subjects
        </Button>
      )}
    </div>
  )
}

function SubjectRow({ subject }: { subject: SubjectListItemData }) {
  const statusStyle = STATUS_STYLES[subject.status] ?? STATUS_STYLES.INACTIVE

  return (
    <div className="rounded-lg border border-border dark:border-border/40 bg-card shadow-sm overflow-hidden border-l-[3px] border-l-cyan-500 dark:border-l-cyan-400">
      <div className="flex items-center gap-3 px-3.5 py-2.5">
        <div className="flex items-center justify-center size-6 rounded-md shrink-0 bg-cyan-100 dark:bg-cyan-900/50">
          <User className="size-3 text-cyan-600 dark:text-cyan-400" strokeWidth={1.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground truncate">
              {subject.displayName ?? subject.primaryIdentifier}
            </span>
            <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0 shrink-0 uppercase font-semibold', statusStyle)}>
              {subject.status}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5 text-[11px] text-muted-foreground">
            {subject.email && <span>{subject.email}</span>}
            {subject.identifierType && <span className="font-mono">{subject.identifierType}</span>}
          </div>
        </div>
        {subject.matchedAttribute && (
          <div className="shrink-0 text-right">
            <div className="text-[10px] text-muted-foreground font-mono">{subject.matchedAttribute.key}</div>
            <div className="text-xs font-medium text-foreground">{String(subject.matchedAttribute.value)}</div>
          </div>
        )}
      </div>
    </div>
  )
}
